// ABOUTME: What PJL adds to one application's detail: a raised rate Zenjob told the talent about, how the first call will
// ABOUTME: happen, and what the talent applied with (rate, hours, week, start), with the way to change the week. On
// ABOUTME: the job page of an application (`jobPage`) each is drawn with that page's sections, rows and notices.
import { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import { Text } from 'components/Text';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import {
  JobPageNotice,
  JobPageRow,
  JobPageSection
} from 'modules/jobListings/components/JobPageParts';
import { formatDay, formatMoney, hoursLabel } from 'modules/jobListings/utils';
import { TalentApplication, TalentApplicationStatus } from '../types';

const Row = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      py: 0.75,
      borderTop: '1px solid',
      borderColor: 'divider'
    }}
  >
    <Text variant="body2" color="text.secondary">
      {label}
    </Text>
    <Text variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
      {value}
    </Text>
  </Box>
);

/** „Neue Vergütung: 17,50 €/Std.“ — only while the application runs; afterwards it no longer applies to anything. */
export const ApplicationNotice = ({
  application,
  jobPage = false
}: {
  application: TalentApplication;
  jobPage?: boolean;
}) => {
  const { notice, status } = application;
  if (!notice || status !== TalentApplicationStatus.ACTIVE) return null;
  if (jobPage) {
    return (
      <JobPageNotice
        testId="application-notice"
        severity="success"
        title={getTransWithParams('applications.detail.notice.raised.title', {
          amount: formatMoney(notice.hourlyRate)
        })}
        body={getTrans('applications.detail.notice.raised.body')}
      />
    );
  }
  return (
    <Alert
      severity="success"
      icon={false}
      data-testid="application-notice"
      sx={{ borderRadius: 2, mx: 2.5, mb: 2 }}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        {getTransWithParams('applications.detail.notice.raised.title', {
          amount: formatMoney(notice.hourlyRate)
        })}
      </AlertTitle>
      {getTrans('applications.detail.notice.raised.body')}
    </Alert>
  );
};

/** The first call: when it may come from the AI assistant, what that means and how it goes best; and when we call. */
export const FirstCallNotice = ({
  application,
  jobPage = false
}: {
  application: TalentApplication;
  jobPage?: boolean;
}) => {
  const { firstCall } = application;
  if (!firstCall) return null;
  if (jobPage) {
    return (
      <Box data-testid="application-first-call">
        {firstCall.window && (
          <Text
            variant="body2"
            color="text.secondary"
            component="p"
            sx={{ mb: 1 }}
          >
            {getTransWithParams('applications.detail.firstCall.window', {
              label: firstCall.window
            })}
          </Text>
        )}
        {firstCall.mode === 'AI' && (
          <JobPageNotice
            title={getTrans('applications.detail.firstCall.ai.title')}
            body={getTrans('applications.detail.firstCall.ai.body')}
          />
        )}
      </Box>
    );
  }
  return (
    <Box data-testid="application-first-call" sx={{ px: 2.5, pb: 2 }}>
      {firstCall.window && (
        <Text
          variant="body2"
          color="text.secondary"
          component="p"
          sx={{ mb: 1 }}
        >
          {getTransWithParams('applications.detail.firstCall.window', {
            label: firstCall.window
          })}
        </Text>
      )}
      {firstCall.mode === 'AI' && (
        <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>
            {getTrans('applications.detail.firstCall.ai.title')}
          </AlertTitle>
          {getTrans('applications.detail.firstCall.ai.body')}
        </Alert>
      )}
    </Box>
  );
};

/** The rate, hours, week and start the talent sent, each only when known, as rows of the given style. */
const AppliedWithRows = ({
  application,
  Row: RowOf
}: {
  application: TalentApplication;
  Row: (props: { label: string; value: string }) => JSX.Element;
}) => {
  const { appliedWith } = application;
  if (!appliedWith) return null;
  return (
    <>
      {appliedWith.hourlyRate !== undefined && (
        <RowOf
          label={getTrans('applications.detail.appliedWith.rate')}
          value={formatMoney(appliedWith.hourlyRate)}
        />
      )}
      {appliedWith.weeklyMinutes !== undefined && (
        <RowOf
          label={getTrans('applications.detail.appliedWith.weeklyHours')}
          value={getTransWithParams('jobs.card.hoursPerWeek', {
            hours: hoursLabel(
              appliedWith.weeklyMinutes,
              appliedWith.weeklyMinutes
            )
          })}
        />
      )}
      {appliedWith.daysSummary && (
        <RowOf
          label={getTrans('applications.detail.appliedWith.week')}
          value={appliedWith.daysSummary}
        />
      )}
      {appliedWith.startFrom && (
        <RowOf
          label={getTrans('applications.detail.appliedWith.start')}
          value={formatDay(appliedWith.startFrom)}
        />
      )}
    </>
  );
};

/** What the application waits for from the talent, as a notice on the job page of the application. */
export const NeedsYouNotice = ({
  application
}: {
  application: TalentApplication;
}) => {
  const { needsAction } = application;
  if (!needsAction) return null;
  return (
    <JobPageNotice
      testId="application-needs-action"
      dataType={needsAction.type}
      severity="warning"
      title={getTrans(
        `applications.needsAction.${needsAction.type}` as TranslationKey
      )}
    />
  );
};

/** The reason of the latest move, when it was shared with the talent, as a notice on the job page of the application. */
export const SharedReasonNotice = ({
  application
}: {
  application: TalentApplication;
}) =>
  application.sharedReason ? (
    <JobPageNotice
      testId="application-shared-reason"
      title={getTrans('applications.card.sharedReason')}
      body={application.sharedReason}
    />
  ) : null;

/**
 * „Damit hast du dich beworben“: the rate of the version applied to, the hours, the week and the start the talent sent.
 * `changeWeek` is the way to change the week, offered only while the application runs.
 */
export const AppliedWith = ({
  application,
  changeWeek,
  jobPage = false
}: {
  application: TalentApplication;
  changeWeek?: ReactNode;
  jobPage?: boolean;
}) => {
  const { appliedWith, status } = application;
  if (!appliedWith) return null;
  if (jobPage) {
    return (
      <JobPageSection
        title={getTrans('applications.detail.appliedWith.title')}
        testId="application-applied-with"
        headingId="application-applied-with-title"
      >
        <AppliedWithRows application={application} Row={JobPageRow} />
        {status === TalentApplicationStatus.ACTIVE && changeWeek && (
          <Box sx={{ mt: 1 }}>
            {changeWeek}
            <Text variant="caption" color="text.secondary" component="p">
              {getTrans('applications.detail.appliedWith.changeHint')}
            </Text>
          </Box>
        )}
      </JobPageSection>
    );
  }
  return (
    <Box
      component="section"
      data-testid="application-applied-with"
      sx={{ px: 2.5, pb: 2 }}
    >
      <Text
        variant="subtitle1"
        component="h2"
        color="primary.main"
        sx={{ fontWeight: 700, mb: 0.5 }}
      >
        {getTrans('applications.detail.appliedWith.title')}
      </Text>
      <AppliedWithRows application={application} Row={Row} />
      {status === TalentApplicationStatus.ACTIVE && changeWeek && (
        <Box sx={{ mt: 1 }}>
          {changeWeek}
          <Text variant="caption" color="text.secondary" component="p">
            {getTrans('applications.detail.appliedWith.changeHint')}
          </Text>
        </Box>
      )}
    </Box>
  );
};
